import React, { useMemo, useState, useContext } from "react";
import { Toaster, toast } from "react-hot-toast";
import {
  Building2,
  Filter,
  RefreshCcw,
  XCircle,
  ReceiptText,
  RotateCcw, Download,
  Calculator,
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { GlobalContext } from "../../Context/GlobalContext";

// ✅ NEW: import Refunds tab component
import AdminRefundsReport from "./AdminRefundsReport";
import useTransactions from "../Services/PaymentServices/useTransactions";
import AdminReviseMapReport from "./AdminReviseMapReport";

const PAGE_SIZE = 200;

export default function AdminAllTransactionsReport() {
  const [activeTab, setActiveTab] = useState("transactions");// "transactions" | "refunds" | "reviseMap"

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />

      {/* Page Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <Building2 size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Transactions, Refunds & Revise Map</h1>
            <p className="text-sm text-gray-500">
              Switch tabs to view transactions, refunds or revise map reports
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-3 rounded-2xl border bg-white p-2">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveTab("transactions")}
            className={`inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-semibold transition ${activeTab === "transactions"
              ? "bg-[#01C37D] text-white"
              : "border bg-white text-gray-700 hover:bg-gray-50"
              }`}
          >
            <ReceiptText size={16} />
            Transactions
          </button>

          <button
            onClick={() => setActiveTab("refunds")}
            className={`inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-semibold transition ${activeTab === "refunds"
               ? "bg-[#EF4444] text-white"
  : "border bg-white text-gray-700 hover:bg-gray-50"
              }`}
          >
            <RotateCcw size={16} />
            Refunds
          </button>
          <button
            onClick={() => setActiveTab("reviseMap")}
            className={`inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-semibold transition ${activeTab === "reviseMap"
              ? "bg-[#705CFE] text-white"
              : "border bg-white text-gray-700 hover:bg-gray-50"
              }`}
          >
            <Calculator size={16} />
            Revise Map
          </button>
        </div>
      </div>

      {/* Tab content */}
      {activeTab === "transactions" ? (
        <TransactionsTab />
      ) : activeTab === "refunds" ? (
        <AdminRefundsReport />
      ) : (
        <AdminReviseMapReport />
      )}
    </div>
  );
}

/* ----------------------------- Transactions Tab ----------------------------- */
/* ✅ This is your existing code moved here with minimal changes:
   - Removed outer padding + Toaster (handled by parent)
   - Everything else same (Apply loads data)
*/
function TransactionsTab() {
  const { getAllTransactionsReportV1, refundProcessAdminApi } =
    useTransactions();

  const { api, adminId } = useContext(GlobalContext);

  // Defaults (yesterday -> today)
  const today = () => new Date().toISOString().slice(0, 10);
  const yday = () => new Date(Date.now() - 86400000).toISOString().slice(0, 10);

  // Filters
  const [fromDate, setFromDate] = useState(yday());
  const [toDate, setToDate] = useState(today());
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | Y | N
  const [modeFilter, setModeFilter] = useState("all"); // all | UPI | IMPS | etc.

  // Data
  const [rows, setRows] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  // Refund modal state
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundRow, setRefundRow] = useState(null);
  const [refundLoading, setRefundLoading] = useState(false);

  /* -------------------- Fetch (ONLY on Apply click) -------------------- */
  const fetchAllTxnReport = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select From & To date");
      return;
    }

    if (fromDate > toDate) {
      toast.error("From date cannot be after To date");
      return;
    }

    setLoading(true);

    try {
      const result = await getAllTransactionsReportV1({
        fromDate,
        toDate,
      });

      if (result.success) {
        setRows(result.data.reverse());
        setPage(1);
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (e) {
      toast.error("Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- Refund -------------------- */
  const isRefunded = (r) => String(r?.is_refunded ?? "").toUpperCase() === "Y";

  const openRefundModal = (row) => {
    if (isRefunded(row)) return;
    setRefundRow(row);
    setRefundOpen(true);
  };

  const closeRefundModal = () => {
    if (refundLoading) return;
    setRefundOpen(false);
    setRefundRow(null);
  };

  const toRoundedAmountString = (amount) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return "";
    return String(Math.round(n)); // 50.000000 -> "50"
  };

  const buildRefundPayload = (r) => {
    return {
      orderId: String(r?.order_id ?? ""),
      userId: String(r?.fk_user_id ?? ""),
      // NOTE: In your current file you set transactionId as va_account.
      // If backend expects pk_log_id or bank_rrn, change this mapping accordingly.
      transactionId: String(r?.va_account ?? ""),
      p1_Account: String(r?.account_no ?? ""),
      p2_ifsccode: String(r?.ifsc ?? ""),
      p4_amount: toRoundedAmountString(r?.amount),
      refundedBy: adminId,
    };
  };

  const refundProcessAdmin = async () => {
    if (!refundRow) return;

    if (isRefunded(refundRow)) {
      toast.error("Already refunded");
      return;
    }

    const payload = buildRefundPayload(refundRow);

    if (!payload.orderId || !payload.userId || !payload.transactionId) {
      toast.error(
        "Missing required refund fields (orderId/userId/transactionId)",
      );
      return;
    }

    if (!payload.p1_Account || !payload.p2_ifsccode || !payload.p4_amount) {
      toast.error("Missing account/ifsc/amount for refund");
      return;
    }

    setRefundLoading(true);

    try {
      const result = await refundProcessAdminApi(payload);

      if (result.success) {
        toast.success(result.message);

        // optimistic UI update
        setRows((prev) =>
          prev.map((x) =>
            x?.pk_log_id === refundRow?.pk_log_id
              ? {
                ...x,
                is_refunded: "Y",
              }
              : x,
          ),
        );

        closeRefundModal();
      } else {
        toast.error(result.message);
      }
    } catch (e) {
      toast.error("Refund API failed");
    } finally {
      setRefundLoading(false);
    }
  };

  /* -------------------- Derived -------------------- */
  const sortedRows = useMemo(() => {
    const parseDate = (v) => {
      const d = new Date(v || 0);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    };
    return [...rows].sort(
      (a, b) => parseDate(b.insert_date) - parseDate(a.insert_date),
    );
  }, [rows]);

  const modes = useMemo(() => {
    const set = new Set();
    for (const r of sortedRows) if (r?.mode) set.add(String(r.mode));
    return ["all", ...Array.from(set)];
  }, [sortedRows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const s = (v) => (v ?? "").toString().toLowerCase();

    return sortedRows.filter((r) => {
      const matchesSearch = !q
        ? true
        : [
          r.pk_log_id,
          r.fk_user_id,
          r.order_id,
          r.customer_mobile,
          r.va_account,
          r.account_no,
          r.ifsc,
          r.sender_name,
          r.sender_acc,
          r.bank_rrn,
          r.mode,
          r.is_refunded,
        ]
          .map(s)
          .some((v) => v.includes(q));

      const matchesStatus =
        statusFilter === "all"
          ? true
          : s(r.status) === statusFilter.toLowerCase();

      const matchesMode =
        modeFilter === "all" ? true : s(r.mode) === modeFilter.toLowerCase();

      return matchesSearch && matchesStatus && matchesMode;
    });
  }, [sortedRows, search, statusFilter, modeFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pageSafe = (p) => Math.min(totalPages, Math.max(1, p));
  const paginated = useMemo(
    () => filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredRows, page],
  );

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setModeFilter("all");
    setPage(1);
  };

  const money = (v) =>
    v == null || isNaN(+v) ? "₹ 0.00" : `₹ ${Number(v).toFixed(2)}`;

  // ✅ Export Excel (exports ALL rows fetched, not just current page)
  const exportExcel = () => {
    if (!rows || rows.length === 0) {
      toast.error("No data to export.");
      return;
    }

    try {
      const filename = `AllTransactionsReport_${fromDate}_to_${toDate}.xlsx`;

      // Export clean JSON:
      // - Keep raw values
      // - Don’t include React badges etc.
      const exportRows = rows.map((r) => ({
        pk_log_id: r?.pk_log_id || "N/A",
        fk_user_id: r?.fk_user_id || "N/A",
        order_id: r?.order_id || "N/A",
        customer_mobile: r?.customer_mobile || "N/A",
        va_account: r?.va_account || "N/A",
        account_no: r?.account_no || "N/A",
        gateway: r?.gateway || "N/A",
        ifsc: r?.ifsc || "N/A",
        sender_name: r?.sender_name || "N/A",
        sender_acc: r?.sender_acc || "N/A",
        insert_date: r?.insert_date || "N/A",
        status: r?.status || "N/A",
        amount: r?.amount || "N/A",
        bank_rrn: r?.bank_rrn || "N/A",
        mode: r?.mode || "N/A",
        is_refunded: r?.is_refunded || "N/A",
        update: r?.update || "N/A",
      }));

      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "AllSettlements");

      const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        filename,
      );

      toast.success("Excel downloaded.");
    } catch {
      toast.error("Failed to export excel.");
    }
  };

  return (
    <div>
      {/* Header (tab section header) */}
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Transactions</h2>
          <p className="text-sm text-gray-500">
            Loads only when you click Apply
          </p>
        </div>

        <button
          className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:opacity-60"
          onClick={fetchAllTxnReport}
          disabled={loading}
          title="Refresh (re-fetch with current date range)"
        >
          <RefreshCcw size={16} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border bg-white p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Filter size={16} /> Filters
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">From</div>
            <input
              type="date"
              className="h-10 w-44 rounded-xl border px-3 text-sm outline-none"
              value={fromDate}
              max={today()}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>

          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">To</div>
            <input
              type="date"
              className="h-10 w-44 rounded-xl border px-3 text-sm outline-none"
              value={toDate}
              max={today()}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>

          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Search</div>
            <div className="flex h-10 w-80 items-center gap-2 rounded-xl border px-3">
              <input
                className="h-full w-full text-sm outline-none"
                placeholder="Order ID / Mobile / VA / Account / IFSC / RRN / Mode"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
              {search && (
                <button
                  className="rounded-full p-1 text-gray-500 hover:bg-gray-100"
                  onClick={() => setSearch("")}
                >
                  <XCircle size={16} />
                </button>
              )}
            </div>
          </label>

          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Status</div>
            <select
              className="h-10 w-40 rounded-xl border bg-white px-3 text-sm outline-none"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All</option>
              <option value="Y">Y</option>
              <option value="N">N</option>
            </select>
          </label>

          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Mode</div>
            <select
              className="h-10 w-44 rounded-xl border bg-white px-3 text-sm outline-none"
              value={modeFilter}
              onChange={(e) => {
                setModeFilter(e.target.value);
                setPage(1);
              }}
            >
              {modes.map((m) => (
                <option key={m} value={m}>
                  {m === "all" ? "All" : m}
                </option>
              ))}
            </select>
          </label>

          <div className="ml-auto flex gap-2">
            {/* Export button (next to filter) */}
            <div className="flex items-end md:col-span-1">
              <button
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-3 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-50"
                onClick={exportExcel}
                disabled={loading || !rows || rows.length === 0}
                title={
                  !rows || rows.length === 0
                    ? "No data to export"
                    : "Export to Excel"
                }
              >
                <Download size={16} />
                Export Excel
              </button>
            </div>
            <button
              className="inline-flex h-10 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50 disabled:opacity-60"
              onClick={clearFilters}
              disabled={loading}
            >
              Clear
            </button>

            <button
              className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white hover:bg-[#01C37D]/90 disabled:opacity-60"
              onClick={fetchAllTxnReport}
              disabled={loading}
            >
              {loading ? "Applying…" : "Apply"}
            </button>
          </div>
        </div>

        <div className="mt-2 text-xs text-gray-500">
          Records: <b className="text-gray-700">{filteredRows.length}</b>
          {rows.length > 0 && (
            <>
              {" "}
              (Fetched: <b className="text-gray-700">{rows.length}</b>)
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="mt-3 overflow-hidden rounded-2xl border bg-white">
        <div className="overflow-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-[#01C37D] text-white">
              <tr>
                {[
                  "S/N",
                  "Log ID",
                  "User ID",
                  "Order ID",
                  "Customer Mobile",
                  "VA Account",
                  "Account No",
                  "Gate Way",
                  "IFSC",
                  "Sender Name",
                  "Sender Acc",
                  "Insert Date",
                  "Status",
                  "Amount",
                  "Bank RRN",
                  "Mode",
                  "Action",
                ].map((h) => (
                  <th key={h} className="px-4 py-2 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan={16}
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    No data loaded yet. Select date range and click <b>Apply</b>
                    .
                  </td>
                </tr>
              )}

              {rows.length > 0 && paginated.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan={16}
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    No records found for current filters.
                  </td>
                </tr>
              )}

              {paginated.map((r, idx) => {
                const st = String(r.status || "").toUpperCase();
                const isY = st === "Y";
                const refunded = isRefunded(r);

                return (
                  <tr
                    key={`${r.pk_log_id}-${idx}`}
                    className={`border-t ${refunded ? "bg-yellow-100" : ""}`}
                  >
                    <td className="px-4 py-2">
                      {(page - 1) * PAGE_SIZE + idx + 1}
                    </td>
                    <td className="px-4 py-2 font-semibold">
                      {r.pk_log_id ?? "—"}
                    </td>
                    <td className="px-4 py-2">{r.fk_user_id ?? "—"}</td>
                    <td className="px-4 py-2 font-semibold">
                      {r.order_id ?? "—"}
                    </td>
                    <td className="px-4 py-2">{r.customer_mobile ?? "—"}</td>
                    <td className="px-4 py-2">{r.va_account ?? "—"}</td>
                    <td className="px-4 py-2">{r.account_no ?? "—"}</td>
                    <td className="px-4 py-2">{r.gateway ?? "—"}</td>
                    <td className="px-4 py-2">
                      {(r.ifsc || "").toUpperCase() || "—"}
                    </td>
                    <td className="px-4 py-2">{r.sender_name ?? "—"}</td>
                    <td className="px-4 py-2">{r.sender_acc ?? "—"}</td>
                    <td className="px-4 py-2">{r.insert_date ?? "—"}</td>

                    <td className="px-4 py-2">
                      {st ? (
                        <span
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${isY
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-rose-200 bg-rose-50 text-rose-700"
                            }`}
                        >
                          <span className="h-1.5 w-1.5 rounded-full bg-current" />
                          {st}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td className="px-4 py-2">{money(r.amount)}</td>
                    <td className="px-4 py-2">{r.bank_rrn ?? "—"}</td>
                    <td className="px-4 py-2">{r.mode ?? "—"}</td>

                    <td className="px-4 py-2">
                      <button
                        className={`h-9 rounded-xl px-3 text-sm font-semibold text-white disabled:opacity-60 ${refunded
                          ? "bg-gray-500"
                          : "bg-[#FC981B] hover:bg-[#FC981B]/90"
                          }`}
                        onClick={() => openRefundModal(r)}
                        disabled={loading || refunded}
                        title={
                          refunded
                            ? "Already refunded"
                            : "Refund this transaction"
                        }
                      >
                        {refunded ? "Refunded" : "Refund"}
                      </button>
                    </td>
                  </tr>
                );
              })}

              {loading && (
                <tr>
                  <td
                    colSpan={16}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredRows.length > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-2 p-3">
            <button
              className="h-9 rounded-xl border px-3 text-sm disabled:opacity-60"
              onClick={() => setPage((p) => pageSafe(p - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <span className="text-sm text-gray-600">
              Page <b>{page}</b> of <b>{totalPages}</b>
            </span>
            <button
              className="h-9 rounded-xl border px-3 text-sm disabled:opacity-60"
              onClick={() => setPage((p) => pageSafe(p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Refund Confirmation Modal */}
      {refundOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeRefundModal}
          />

          <div className="relative w-full max-w-2xl rounded-2xl bg-white p-4 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Confirm Refund</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Refund amount is always sent as <b>rounded value</b> (e.g.
                  50.000000 → 50).
                </p>
              </div>

              <button
                className="rounded-xl border px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-60"
                onClick={closeRefundModal}
                disabled={refundLoading}
              >
                Close
              </button>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              {(() => {
                const p = refundRow ? buildRefundPayload(refundRow) : null;
                if (!p) return null;

                const item = (label, value) => (
                  <div className="rounded-xl border p-3">
                    <div className="text-xs text-gray-500">{label}</div>
                    <div className="mt-1 break-all text-sm font-semibold text-gray-800">
                      {value || "—"}
                    </div>
                  </div>
                );

                return (
                  <>
                    {item("Order ID", p.orderId)}
                    {item("User ID", p.userId)}
                    {item("Transaction ID", p.transactionId)}
                    {item("Account (p1_Account)", p.p1_Account)}
                    {item("IFSC (p2_ifsccode)", p.p2_ifsccode)}
                    {item("Amount (p4_amount)", p.p4_amount)}
                  </>
                );
              })()}
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
              <button
                className="h-10 rounded-xl border px-4 text-sm hover:bg-gray-50 disabled:opacity-60"
                onClick={closeRefundModal}
                disabled={refundLoading}
              >
                Cancel
              </button>

              <button
                className="h-10 rounded-xl bg-[#BE2908] px-4 text-sm font-semibold text-white hover:bg-[#BE2908]/90 disabled:opacity-60"
                onClick={refundProcessAdmin}
                disabled={refundLoading}
              >
                {refundLoading ? "Processing..." : "Yes, Refund"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
